import React, { useEffect, useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  List, 
  ListItem, 
  ListItemText, 
  Radio,
  TextField,
  Divider,
  CircularProgress,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import { useDispatch, useSelector } from 'react-redux';
import { closeBasketModal, openOrderWindow } from '../../../store/actions/orders';
import { fetchBasketOrdersAction, createBasketAction } from '../../../store/actions/basket';
import { getEffectiveOrderPayload } from '../../../services/utilities/orderUtils';
import { useSnackbar } from 'notistack';
import { styles } from './AddToBasketModalCSS';

const AddToBasketModal = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  
  const { open, instrument } = useSelector(state => state.orders.basketModal || { open: false, instrument: null });
  const baskets = useSelector(state => state.orders.arrBasketData || []);
  const userSettings = useSelector(state => state.profile.userSettings);
  const positionsArray = useSelector(state => state.portfolio?.arrPositionsUpdateData || []);
  
  const [loading, setLoading] = useState(false);
  const [selectedBasketId, setSelectedBasketId] = useState('');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newBasketName, setNewBasketName] = useState('');
  const [creating, setCreating] = useState(false);
  
  // New State for Hedge Basket options
  const [basketType, setBasketType] = useState('NORMAL');
  const [productType, setProductType] = useState('MIS');
  const [orderType, setOrderType] = useState('MARKET');

  useEffect(() => {
    if (open) {
      loadBaskets();
      setIsCreatingNew(false);
      setNewBasketName('');
      setSelectedBasketId('');
    }
  }, [open]);

  const loadBaskets = async () => {
    setLoading(true);
    await dispatch(fetchBasketOrdersAction());
    setLoading(false);
  };

  const handleClose = () => {
    dispatch(closeBasketModal());
  };

  const handleCreateBasket = async () => {
    if (newBasketName.trim().length < 2) {
      enqueueSnackbar('Basket name must be at least 2 characters', { variant: 'warning' });
      return;
    }
    
    setCreating(true);
    const resolvedProductType = basketType === "NORMAL" ? "ALL" : productType;
    const resolvedOrderType = basketType === "NORMAL" ? "ALL" : orderType;

    const res = await dispatch(createBasketAction({ 
      name: newBasketName,
      login_id: localStorage.getItem("login_id"),
      type: basketType,
      product_type: resolvedProductType,
      order_type: resolvedOrderType
    }));
    setCreating(false);
    
    if (res.status === 'success') {
      setIsCreatingNew(false);
      
      // Auto-select and proceed with the newly created basket
      if (res.data && res.data.basket_id) {
        setSelectedBasketId(res.data.basket_id);
        
        // We close the basket modal first
        dispatch(closeBasketModal());

        // Format the instrument payload for the Order Window
        const orderSide = instrument?.orderSide || instrument?.order_side || 'BUY';
        const basePayload = getEffectiveOrderPayload(instrument, orderSide, userSettings, positionsArray);
        
        const orderData = {
          ...basePayload,
          is_modify: false,
          source: 'BASKET',
          basketDetails: {
            basketId: res.data.basket_id,
            basketName: res.data.name || newBasketName
          },
          quantity: instrument?.quantity || instrument?.lotsize || 1
        };

        dispatch(openOrderWindow(orderData));
      }
      
      setNewBasketName('');
      // Reset state
      setBasketType('NORMAL');
      setProductType('MIS');
      setOrderType('MARKET');
    } else {
      enqueueSnackbar(res.message || 'Failed to create basket', { variant: 'error' });
    }
  };

  const handleProceed = () => {
    if (!selectedBasketId) return;

    const selectedBasket = baskets.find(b => b.basket_id === selectedBasketId);
    if (!selectedBasket) return;

    // We close the basket modal first
    dispatch(closeBasketModal());

    // Format the instrument payload for the Order Window
    // In basket mode, we pass source='BASKET' and the basket details
    const orderSide = instrument?.orderSide || instrument?.order_side || 'BUY';
    const basePayload = getEffectiveOrderPayload(instrument, orderSide, userSettings, positionsArray);
    
    const orderData = {
      ...basePayload,
      is_modify: false,
      source: 'BASKET',
      basketDetails: {
        basketId: selectedBasket.basket_id,
        basketName: selectedBasket.name
      },
      quantity: instrument.quantity || instrument.lotsize || 1
    };

    dispatch(openOrderWindow(orderData));
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle sx={styles.dialogTitle}>
        <Typography variant="h6" fontWeight={600}>Add to Basket</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={styles.dialogContent}>
        {loading ? (
          <Box sx={styles.loadingBox}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <Box sx={styles.mainBox}>
            {baskets.length === 0 && !isCreatingNew ? (
              <Box sx={styles.emptyBox}>
                <Typography color="text.secondary" gutterBottom>You don't have any baskets.</Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<AddIcon />} 
                  onClick={() => setIsCreatingNew(true)}
                  sx={styles.emptyButton}
                >
                  Create New Basket
                </Button>
              </Box>
            ) : (
              <>
                {!isCreatingNew ? (
                  <List sx={styles.list}>
                    {baskets.map((basket) => {
                      const matchingOrder = basket.orders && basket.orders.find(order => 
                        String(order.order_info?.instrument_token) === String(instrument?.token || instrument?.instrument_token) && 
                        String(order.order_info?.exchange) === String(instrument?.exchange)
                      );

                      return (
                        <ListItem 
                          key={basket.basket_id}
                          button 
                          onClick={() => setSelectedBasketId(prev => prev === basket.basket_id ? null : basket.basket_id)}
                          sx={styles.listItem}
                          secondaryAction={
                            matchingOrder ? (
                              <ShoppingBasketIcon fontSize="small" sx={styles.shoppingBasketIcon} />
                            ) : null
                          }
                        >
                          <Radio 
                            checked={selectedBasketId === basket.basket_id}
                            onClick={(e) => {
                              // Prevent double firing if ListItem onClick also handles it
                              e.stopPropagation();
                              setSelectedBasketId(prev => prev === basket.basket_id ? null : basket.basket_id);
                            }}
                            size="small"
                          />
                          <ListItemText 
                            primary={basket.name} 
                            secondary={`${basket.orders ? basket.orders.length : 0} items`} 
                          />
                        </ListItem>
                      );
                    })}
                    
                    <ListItem button onClick={() => setIsCreatingNew(true)} sx={styles.createNewListItem}>
                      <AddIcon sx={styles.addIcon} fontSize="small" />
                      <ListItemText primary="Create New Basket" primaryTypographyProps={{ fontWeight: 500 }} />
                    </ListItem>
                  </List>
                ) : (
                  <Box sx={styles.createNewBox}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>Create New Basket</Typography>
                    
                    <TextField
                      autoFocus
                      fullWidth
                      size="small"
                      placeholder="Enter basket name"
                      value={newBasketName}
                      onChange={(e) => setNewBasketName(e.target.value)}
                      variant="outlined"
                      sx={styles.textField}
                    />



                    <Box sx={styles.actionsBox}>
                      <Button size="small" onClick={() => {
                        setIsCreatingNew(false);
                        setNewBasketName('');
                        setBasketType('NORMAL');
                        setProductType('MIS');
                        setOrderType('MARKET');
                      }}>
                        Cancel
                      </Button>
                      <Button 
                        size="small" 
                        variant="contained" 
                        onClick={handleCreateBasket}
                        disabled={creating || newBasketName.trim().length < 2}
                      >
                        {creating ? 'Creating...' : 'Create'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      
      {!isCreatingNew && (
        <DialogActions sx={styles.dialogActions}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleProceed} 
            variant="contained" 
            disabled={!selectedBasketId}
          >
            Proceed
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AddToBasketModal;
